import React, { useEffect, useRef, useState } from 'react';
import Button, { ButtonProps } from './Button';
import type { IconName } from './Icon';
import { useToast } from '../contexts/ToastContext';

export interface ActionButtonProps
    extends Omit<ButtonProps, 'loading' | 'success' | 'onClick'> {
    /** Async action fired on click. Contract (mirrors {@link SaveButton}):
     *    - resolves with any value **other than** ``false`` (including
     *      ``undefined``) → success path runs: the button turns green with a
     *      check for ``successMs`` ms and ``onSuccess`` fires.
     *    - resolves with ``false`` → treated as an aborted action (e.g. a local
     *      validation that already toasted its reason) — no success animation.
     *    - throws → no success animation. If ``formatError`` is set the error is
     *      surfaced centrally as a toast; otherwise the handler is expected to
     *      have toasted itself. */
    onAction: () => Promise<boolean | void>;
    /** Runs once, right as the button flips to green after a successful action.
     *  Use it to refresh data or to close a modal a beat later so the user
     *  actually sees the confirmation. */
    onSuccess?: () => void;
    /** When provided, a thrown ``onAction`` is caught and
     *  ``toast.error(formatError(err))`` is shown — so callers can simply
     *  ``throw``. Omit to keep the "handler toasts itself" contract. */
    formatError?: (err: unknown) => string;
    /** Icon shown in the success state (default ``check``). */
    successIcon?: IconName;
    /** Label shown briefly in the success state. Falls back to ``children``. */
    successLabel?: React.ReactNode;
    /** How long the green success state stays visible, in ms (default 1500). */
    successMs?: number;
}

const DEFAULT_SUCCESS_MS = 1500;

/**
 * Action button with built-in spin→green-check feedback for one-shot async
 * actions (send invitation, connect an integration, run an import…). States:
 *
 *   - **Idle**: the given ``icon`` + label.
 *   - **Running**: spinning ``progress_activity``, disabled.
 *   - **Success**: green ``check`` pulse for ``successMs`` ms.
 *
 * It is the sibling of {@link SaveButton} for actions that are *not* form saves
 * (no dirty tracking, no "Save" semantics). Both lean on {@link Button}'s
 * ``loading`` / ``success`` props so the visual stays identical everywhere.
 *
 * ## How to configure it per situation
 *
 * | Situation | Configuration |
 * |---|---|
 * | Simple action that stays on screen (connect, recalc, test, resend) | `onAction={async () => { await api.x() }}` — nothing else. |
 * | Refresh data when done | add `onSuccess={() => reload()}`. |
 * | Inside a modal that should close on success | `onSuccess={() => window.setTimeout(onClose, 1200)}` — the delay lets the green check show before it closes (see `InviteUserModal`). |
 * | Local validation that aborts without animating | return `false` from `onAction` (it already toasted the reason) → stays idle, no green. |
 * | API error | `throw` in `onAction`; pass `formatError` (tenant: `translateApiError(e,t,key)`; admin: a plain Spanish string) to surface a toast centrally — else the handler toasts itself. On error it never turns green. |
 * | Success label | by default DON'T pass `successLabel` (just turns green + check; zero i18n keys). Use it (with a `t()` key in tenant) only where it reads better ("Enviado"). |
 * | Icons | `icon` = idle, `successIcon` = success (default `check`). |
 * | Icon-only row button | `size="sm"` + `icon`, no children. |
 * | Role-gated write action | `requires="write" \| "config"` (passed through to {@link Button} / WriteGuard). |
 * | Green duration | `successMs` (default 1500). |
 *
 * **Do NOT use it for:** form saves → {@link SaveButton} (tenant) or a
 * `<Button type="submit" success={saved}>` driven by `useSavedPulse` (admin);
 * destructive actions → {@link ConfirmDialog} with `loading` (no green check).
 * Full guide in `documentation/Standards.md` → "ActionButton".
 */
const ActionButton: React.FC<ActionButtonProps> = ({
    onAction,
    onSuccess,
    formatError,
    icon,
    successIcon = 'check',
    successLabel,
    successMs = DEFAULT_SUCCESS_MS,
    disabled,
    children,
    ...rest
}) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const timer = useRef<number | undefined>(undefined);

    useEffect(
        () => () => {
            if (timer.current) window.clearTimeout(timer.current);
        },
        [],
    );

    const handleClick = async () => {
        if (loading || succeeded) return;
        setLoading(true);
        try {
            const result = await onAction();
            if (result === false) return;
            setSucceeded(true);
            timer.current = window.setTimeout(() => setSucceeded(false), successMs);
            onSuccess?.();
        } catch (err) {
            if (formatError) toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            {...rest}
            type={rest.type ?? 'button'}
            icon={icon}
            loading={loading}
            success={succeeded}
            successIcon={successIcon}
            disabled={disabled || succeeded}
            onClick={handleClick}
        >
            {succeeded && successLabel != null ? successLabel : children}
        </Button>
    );
};

export default ActionButton;
